import { Alert, AlertTitle, Button, Checkbox, Chip, FormControlLabel, FormGroup, FormHelperText, Icon } from '@mui/material';
import React, { useEffect, useState, useTransition } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getUploadComplete, getUploadData, resetFileUpload } from '../../store/upload/uploadSlice';
import DuplicateField from '../DuplicateField';
import { updateNotifyPref } from '../../store/processing/processingSlice';
import { getForceName } from '../../utils/api';
import AlertDialog from '../AlertDialog';
import { Stack } from '@mui/system';
import { useUploadFormStepper } from '../Tabs/Upload/UploadFormContext';
import { ENRICHMENT_OPTIONS } from '../../utils/enrich';

interface ProcessProps {
	forceDisabled?: boolean;
	operation: string;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	pipelineName?: string;
	publishError?: string;
	setPublishError?: (e: string) => void;
}

const ProcessButton: React.FC<ProcessProps> = ({ onClick, operation, forceDisabled, pipelineName, publishError, setPublishError }) => {
	const dispatch = useAppDispatch();
	const uploadComplete = useAppSelector((state) => getUploadComplete(state));
	const notifyOnComplete = useAppSelector((state) => state.processing.notifyOnComplete);
	const selectedUseCase = useAppSelector((state) => state.upload.useCase);
	const isDefaultOp = Boolean(operation === 'default-operation');
	const orgs = useAppSelector((state) => state.user.orgs);
	const forceDetails = useAppSelector((state) => state.system.forceDetails);
	const [forceName, setForceName] = useState('');
	const [rrdDays, setRrdDays] = useState(0);
	const now = new Date();
	const rrdDate = new Date(now.getTime() + rrdDays * 24 * 60 * 60 * 1000);
	const emailEnabled = import.meta.env.VITE_ENABLE_EMAIL || 'true';
	const [isLoadingForce, startTransition] = useTransition();
	const isAdmin = useAppSelector((state) => state.tab.userRoles).includes('ADMINISTRATOR');
	const disabledConditions = [!uploadComplete, forceDisabled, isLoadingForce, isDefaultOp && !isAdmin, !selectedUseCase];
	const { enrichments } = useAppSelector((state) => getUploadData(state));

	const { handleBack } = useUploadFormStepper();

	useEffect(() => {
		startTransition(() => {
			const name = getForceName(
				orgs.map((org) => org.name),
				forceDetails
			);
			setForceName(name);
			if (forceDetails[name] && selectedUseCase) {
				setRrdDays(forceDetails[name].useCases[selectedUseCase.toLocaleUpperCase()].rrdDays);
			}
		});
	}, [selectedUseCase]);

	return (
		<Stack gap={1}>
			<DuplicateField forceDisabled={!uploadComplete} />

			{(emailEnabled === 'true' || process.env.NODE_ENV === 'test') && (
				<FormGroup>
					<FormControlLabel
						control={
							<Checkbox
								data-testid={`notify-email-checkbox`}
								checked={notifyOnComplete}
								onChange={(_, checked) => dispatch(updateNotifyPref(checked))}
								name={'notify-email-checkbox'}
								slotProps={{
									input: {
										'aria-label': 'Notify via Email Checkbox',
									},
								}}
							/>
						}
						label="Check here if you would like to be notified via email when processing has finished"
					/>
					<FormHelperText>
						Please note that this will require your organisation to have allowed the receipt of emails from{' '}
						{import.meta.env.VITE_CONTACT_EMAIL} to the email your account is registered with
					</FormHelperText>
					<FormHelperText>If you are unsure which email this is, click the icon above your name at the top of the page</FormHelperText>
				</FormGroup>
			)}

			{enrichments.length > 0 && (
				<Alert severity="info">
					<AlertTitle>The following enrichments will start after processing has completed:</AlertTitle>
					<Stack direction="row" gap={1} alignItems="center">
						{enrichments.map((item) => (
							<Chip
								icon={<Icon component={ENRICHMENT_OPTIONS[item]?.icon} />}
								color="primary"
								key={item}
								label={ENRICHMENT_OPTIONS[item]?.label}
							/>
						))}
					</Stack>
				</Alert>
			)}

			{!isLoadingForce && Boolean(rrdDays) && selectedUseCase && (
				<Alert severity="warning">{`Be aware that because you are in ${forceName}, all uploaded data will be subject to the agreed RRD policy (${rrdDays} days). Your data will expire on ${rrdDate.toLocaleDateString()}`}</Alert>
			)}

			<Stack direction="row" gap={1}>
				<Button sx={{ flex: '0.1' }} variant="outlined" onClick={handleBack}>
					Back
				</Button>
				<Button
					variant="contained"
					onClick={onClick}
					size="large"
					disabled={disabledConditions.some((con) => con)}
					data-testid="process-button"
					sx={{ flex: 1 }}
				>
					<Stack direction="row" gap={1} alignItems="center">
						{`Start ${pipelineName ?? ''} Processing`}
					</Stack>
				</Button>
			</Stack>

			<AlertDialog
				title="Error submitting message"
				message={[`There was an error submitting the message to the pipeline:`, publishError ?? '', 'How do you want to proceed?']}
				isOpen={Boolean(publishError)}
				rightButtonMessage={'Cancel'}
				rightButtonAction={() => {
					if (setPublishError) setPublishError('');
					dispatch(resetFileUpload());
				}}
				leftButtonMessage={'Retry'}
				leftButtonAction={() => {
					if (setPublishError) setPublishError('');
				}}
			/>
		</Stack>
	);
};

export default ProcessButton;
